// API helper with Bearer token authentication (Cognito access token).

// Backend base URL — empty means relative paths, which the dev server proxy
// (src/setupProxy.js) forwards to the backend. This avoids CORS in development.
// In production it is the API Gateway URL from .env.production.
const BACKEND_URL = process.env.REACT_APP_BLOG_BACKEND || '';

export const API_ENDPOINTS = {
  BLOGS: process.env.REACT_APP_API_BLOGS || `${BACKEND_URL}/api/blogs`,
  SEARCH: process.env.REACT_APP_API_SEARCH || `${BACKEND_URL}/api/search`,
  S3_PRESIGNED_URL:
    process.env.REACT_APP_S3_PRESIGNED_URL_ENDPOINT || `${BACKEND_URL}/api/s3/presigned-url`,
};

export const COGNITO_CONFIG = {
  DOMAIN: process.env.REACT_APP_COGNITO_DOMAIN || '',
  CLIENT_ID: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
  REDIRECT_URI:
    process.env.REACT_APP_COGNITO_REDIRECT_URI ||
    (typeof window !== 'undefined' ? `${window.location.origin}/callback` : ''),
};

// PKCE helpers
const base64UrlEncode = (arrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const generateCodeVerifier = () => {
  const array = new Uint8Array(32);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(array);
  } else {
    // fallback — not secure, but only for environments without Web Crypto
    for (let i = 0; i < array.length; i++) array[i] = Math.floor(Math.random() * 256);
  }
  return base64UrlEncode(array);
};

const generateCodeChallenge = async (verifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await (window.crypto.subtle || window.crypto.webkitSubtle).digest('SHA-256', data);
  return base64UrlEncode(digest);
};

/**
 * Initiate a login using PKCE. Generates a code_verifier and code_challenge,
 * stores the verifier in localStorage and returns the hosted UI login URL.
 */
export const initiateLoginWithPKCE = async () => {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  try {
    localStorage.setItem('pkce_code_verifier', verifier);
  } catch (err) {
    // ignore localStorage errors
  }

  const params = new URLSearchParams({
    client_id: COGNITO_CONFIG.CLIENT_ID,
    response_type: 'code',
    scope: 'email openid phone',
    redirect_uri: COGNITO_CONFIG.REDIRECT_URI,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  return `${COGNITO_CONFIG.DOMAIN}/login?${params.toString()}`;
};

// The hosted-UI URL that starts the login flow (authorization code grant).
export const buildLoginUrl = () =>
  `${COGNITO_CONFIG.DOMAIN}/login?client_id=${COGNITO_CONFIG.CLIENT_ID}` +
  `&response_type=code&scope=email+openid+phone` +
  `&redirect_uri=${encodeURIComponent(COGNITO_CONFIG.REDIRECT_URI)}`;

const TOKEN_KEYS = [
  'isAdmin',
  'accessToken',
  'idToken',
  'authCode',
  'tokenType',
  'tokenTimestamp',
  'expiresIn',
];

export const getAccessToken = () => localStorage.getItem('accessToken');
export const getIdToken = () => localStorage.getItem('idToken');

/**
 * The JWT sent to the backend as `Authorization: Bearer <token>`.
 *
 * The ACCESS token, matching BACKEND_SPEC.md §6 and the sibling Offerleo admin,
 * which authenticates against the same Cognito app client. Falls back to the ID
 * token so a pool that only carries the admin claim there still works rather than
 * failing closed with no session at all.
 */
export const getAuthToken = () => getAccessToken() || getIdToken();

export const clearSession = () => {
  TOKEN_KEYS.forEach((k) => localStorage.removeItem(k));
};

/**
 * Clear the session and restart the login flow — call this on a 401.
 *
 * Back to /admin, not the homepage: apiCall is only ever used by admin writes, and
 * /admin with no session redirects straight to the Cognito hosted UI. Dropping the
 * author on the public site would make them navigate back by hand.
 */
export const clearSessionAndRedirect = () => {
  clearSession();
  window.location.href = '/admin';
};

export const isTokenExpired = () => {
  const timestamp = localStorage.getItem('tokenTimestamp');
  const expiresIn = localStorage.getItem('expiresIn');
  if (!timestamp || !expiresIn) return true;
  return Date.now() > parseInt(timestamp, 10) + parseInt(expiresIn, 10) * 1000;
};

export const isLoggedIn = () =>
  localStorage.getItem('isAdmin') === 'true' && !!getAuthToken() && !isTokenExpired();

/** True when an identity provider is actually configured. */
export const AUTH_CONFIGURED = Boolean(COGNITO_CONFIG.DOMAIN && COGNITO_CONFIG.CLIENT_ID);

/**
 * Authenticated fetch. Every call carries the Cognito JWT, and throws rather than
 * sending a request without one — there is no unauthenticated path, in any build.
 * The backend is still the authority: a 401 clears the session and sends the
 * author back through the hosted UI.
 */
export const apiCall = async (url, options = {}) => {
  const token = getAuthToken();

  if (!token || isTokenExpired()) {
    throw new Error(
      token ? 'Session expired. Please log in again.' : 'Not logged in. Please log in again.'
    );
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (response.status === 401) clearSessionAndRedirect();
  return response;
};

export const apiGet = (url) => apiCall(url, { method: 'GET' });
export const apiPost = (url, data) => apiCall(url, { method: 'POST', body: JSON.stringify(data) });
export const apiPut = (url, data) => apiCall(url, { method: 'PUT', body: JSON.stringify(data) });
export const apiDelete = (url) => apiCall(url, { method: 'DELETE' });
